import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { ONBOARDING_CHECKLIST_POSITION_KEY } from "./constants";

const EDGE_GAP = 8;
// Below this the gesture is a click, so the pill still opens on a shaky tap.
const DRAG_THRESHOLD = 4;

interface Offset {
  x: number;
  y: number;
}

interface OffsetBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const ZERO_OFFSET: Offset = { x: 0, y: 0 };

const readStoredOffset = (): Offset => {
  try {
    const raw = localStorage.getItem(ONBOARDING_CHECKLIST_POSITION_KEY);
    if (!raw) return ZERO_OFFSET;

    const parsed: unknown = JSON.parse(raw);
    const { x, y } = (parsed ?? {}) as Partial<Offset>;

    return typeof x === "number" && typeof y === "number"
      ? { x, y }
      : ZERO_OFFSET;
  } catch {
    return ZERO_OFFSET;
  }
};

const storeOffset = (offset: Offset) => {
  try {
    localStorage.setItem(
      ONBOARDING_CHECKLIST_POSITION_KEY,
      JSON.stringify(offset)
    );
  } catch {
    // A full or blocked storage only costs the saved position, so keep dragging.
  }
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// Bounds are expressed as offsets, so the element keeps whatever CSS anchors it
// and stays responsive instead of being pinned to absolute coordinates.
const offsetBounds = (rect: DOMRect, offset: Offset): OffsetBounds => {
  const anchorLeft = rect.left - offset.x;
  const anchorTop = rect.top - offset.y;

  return {
    minX: EDGE_GAP - anchorLeft,
    maxX: Math.max(
      EDGE_GAP - anchorLeft,
      window.innerWidth - EDGE_GAP - rect.width - anchorLeft
    ),
    minY: EDGE_GAP - anchorTop,
    maxY: Math.max(
      EDGE_GAP - anchorTop,
      window.innerHeight - EDGE_GAP - rect.height - anchorTop
    )
  };
};

const isDragIgnored = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest("[data-drag-ignore]"));

interface DragState {
  pointerX: number;
  pointerY: number;
  startOffset: Offset;
  bounds: OffsetBounds;
}

// One instance drives both the pill and the panel, so expanding keeps the spot
// the user dragged to. Anchors match (bottom-left), so the same offset lines them up.
export const useDraggable = () => {
  const containerRef = useRef<HTMLElement | null>(null);
  const [offset, setOffset] = useState<Offset>(readStoredOffset);
  const dragStateRef = useRef<DragState | null>(null);
  const latestOffsetRef = useRef<Offset>(offset);
  const draggedRef = useRef(false);

  const keepInViewport = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;

    setOffset((current) => {
      const bounds = offsetBounds(element.getBoundingClientRect(), current);
      const next = {
        x: clamp(current.x, bounds.minX, bounds.maxX),
        y: clamp(current.y, bounds.minY, bounds.maxY)
      };

      if (next.x === current.x && next.y === current.y) return current;

      latestOffsetRef.current = next;
      return next;
    });
  }, []);

  // A callback ref because the pill and the panel swap places on the same hook:
  // each one that attaches gets re-clamped, since their sizes differ a lot.
  const setContainer = useCallback(
    (element: HTMLElement | null) => {
      containerRef.current = element;
      if (element) keepInViewport();
    },
    [keepInViewport]
  );

  useEffect(() => {
    window.addEventListener("resize", keepInViewport);
    return () => window.removeEventListener("resize", keepInViewport);
  }, [keepInViewport]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    const element = containerRef.current;
    if (!element || event.button !== 0 || isDragIgnored(event.target)) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    draggedRef.current = false;
    dragStateRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      startOffset: offset,
      bounds: offsetBounds(element.getBoundingClientRect(), offset)
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const state = dragStateRef.current;
    if (!state) return;

    const deltaX = event.clientX - state.pointerX;
    const deltaY = event.clientY - state.pointerY;

    if (
      !draggedRef.current &&
      Math.abs(deltaX) < DRAG_THRESHOLD &&
      Math.abs(deltaY) < DRAG_THRESHOLD
    )
      return;

    draggedRef.current = true;
    const next = {
      x: clamp(
        state.startOffset.x + deltaX,
        state.bounds.minX,
        state.bounds.maxX
      ),
      y: clamp(
        state.startOffset.y + deltaY,
        state.bounds.minY,
        state.bounds.maxY
      )
    };
    latestOffsetRef.current = next;
    setOffset(next);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLElement>) => {
    if (!dragStateRef.current) return;

    dragStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);

    if (draggedRef.current) storeOffset(latestOffsetRef.current);
  };

  return {
    containerRef: setContainer,
    dragStyle: {
      transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`
    } satisfies CSSProperties,
    handleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
      style: { touchAction: "none" } satisfies CSSProperties
    },
    // Lets a click handler ignore the pointerup that ends a drag.
    wasDragged: () => draggedRef.current
  };
};

export type DragHandle = ReturnType<typeof useDraggable>;
